/**
 * Session-scoped snooze store for the Alert Bar (AC4).
 * Dismissing (X) an alert records its id in `sessionStorage` and the orchestrator hook
 * filters it out client-side — no API call. Cleared when the browser session ends, so a
 * snoozed alert reappears at next login.
 */
export const SNOOZE_STORAGE_KEY = 'b2o-alert-bar-snoozed';

/** Read the snoozed ids for this session; resilient to missing/corrupt storage. */
export const getSnoozedIds = (): string[] => {
  try {
    const raw = sessionStorage.getItem(SNOOZE_STORAGE_KEY);
    if (raw == null || raw.length === 0) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((value): value is string => typeof value === 'string');
  } catch {
    return [];
  }
};

/** Record an alert id as snoozed for this session (deduped). No-op if storage is unavailable. */
export const addSnoozedId = (id: string): void => {
  try {
    const current = getSnoozedIds();
    if (current.includes(id)) {
      return;
    }
    sessionStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify([...current, id]));
  } catch {
    // sessionStorage unavailable (e.g. privacy mode) — snooze degrades to no-op.
  }
};

/**
 * Clear all snoozed ids. Called on logout so snoozed alerts reappear at next login (AC4)
 * and cannot leak to another account in the same tab on a shared device.
 */
export const clearSnoozedIds = (): void => {
  try {
    sessionStorage.removeItem(SNOOZE_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
};
